import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Avatar,
  useTheme,
  alpha,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

// Icons
import {
  Code as SnippetIcon,
  Description as DocIcon,
  Folder as FolderIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  AutoFixHigh as AIIcon,
  Storage as StorageIcon
} from '@mui/icons-material';

// Components
import StatsCard from '../components/dashboard/StatsCard';
import QuickActionButton from '../components/dashboard/QuickActionButton';
import GlassCard from '../components/dashboard/GlassCard';

// Hooks
import { useAuth } from '../context/AuthContext';
import { useAppTheme } from '../utils/themeUtils';
import { useAI } from '../context/AIContext';
import dashboardService from '../services/dashboardService';

// Styles
import '../components/dashboard/Dashboard.css';

// Styled Components
const DashboardContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  maxWidth: '1400px',
  position: 'relative',
  
  // Background decoration
  '&::before': {
    content: '""',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(circle at 20% 20%, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, ${alpha(theme.palette.secondary.main, 0.08)} 0%, transparent 50%),
      radial-gradient(circle at 40% 60%, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 50%)
    `,
    zIndex: -1,
    pointerEvents: 'none'
  }
}));

const WelcomeSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  position: 'relative'
}));

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth() || { user: null };
  const { colors } = useAppTheme();
  const theme = useTheme();

  // State for dashboard data
  const [stats, setStats] = useState({
    snippets: 0,
    docs: 0,
    folders: 0,
    views: 0,
    publicItems: 0,
    privateItems: 0,
    itemsThisWeek: 0,
    itemsThisMonth: 0,
    tokensUsed: 0,
    storageUsed: '0 B',
    realViews: 0,
    viewsThisWeek: 0,
    viewsThisMonth: 0
  });

  const [loading, setLoading] = useState(true); // Start with loading true
  const [error, setError] = useState(null);

  // Fetch only essential dashboard data (Quick Stats)
  useEffect(() => {
    const fetchEssentialData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // ONLY fetch stats data - the most important for dashboard
        const statsData = await dashboardService.getStats();
        setStats(statsData);
        
      } catch (error) {
        console.error('❌ Dashboard: Error fetching stats:', error);
        setError('Failed to load dashboard stats');
        setStats({
          snippets: 0, docs: 0, folders: 0, views: 0,
          publicItems: 0, privateItems: 0, itemsThisWeek: 0, itemsThisMonth: 0,
          tokensUsed: 0, storageUsed: '0 B', realViews: 0, viewsThisWeek: 0, viewsThisMonth: 0
        });
      } finally {
        setLoading(false);
      }
    };

    if (user?.userId || user?.id || user?.sub) {
      fetchEssentialData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const handleQuickAction = (action) => {
    switch (action) {
      case 'snippet':
        navigate('/create');
        break;
      case 'doc':
        navigate('/docs');
        break;

      default:
        break;
    }
  };

  return (
    <DashboardContainer>
      {/* Products */}
      <Box sx={{ mb: 5 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <Typography 
            variant="h5" 
            fontWeight="bold" 
            sx={{ 
              mb: 3, 
              color: theme.palette.text.primary,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            Products
          </Typography>
        </motion.div>
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <QuickActionButton
              icon={SnippetIcon}
              title="P<i>AI</i> Snip"
              description="Create a new code snippet"
              onClick={() => handleQuickAction('snippet')}
              color="#6366F1"
              shortcut="⌘+N"
              delay={0.1}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <QuickActionButton
              icon={EditIcon}
              title="P<i>AI</i> Doc"
              description="Start writing documentation"
              onClick={() => handleQuickAction('doc')}
              color="#8B5CF6"
              shortcut="⌘+D"
              delay={0.2}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Quick Stats */}
      <Box sx={{ mb: 5 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Typography 
            variant="h5" 
            fontWeight="bold" 
            sx={{ 
              mb: 3, 
              color: theme.palette.text.primary,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            Quick Stats
          </Typography>
        </motion.div>
        
        {/* Error Display */}
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error} - Showing cached data
          </Alert>
        )}
        
        <Grid container spacing={3}>
          {/* Row 1: Core Content Stats */}
          <Grid item xs={6} sm={6} md={3}>
            <StatsCard
              icon={SnippetIcon}
              value={loading ? '...' : stats.snippets}
              label="Snippets"
              color="#6366F1"
              trend={{ 
                value: stats.itemsThisWeek || 0, 
                period: 'this week' 
              }}
              delay={0.4}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatsCard
              icon={DocIcon}
              value={loading ? '...' : stats.docs}
              label="Documents"
              color="#8B5CF6"
              trend={{ 
                value: stats.itemsThisWeek || 0, 
                period: 'this week' 
              }}
              delay={0.5}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatsCard
              icon={FolderIcon}
              value={loading ? '...' : stats.folders}
              label="Folders"
              color="#10B981"
              trend={{ 
                value: stats.itemsThisMonth || 0, 
                period: 'this month' 
              }}
              delay={0.6}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatsCard
              icon={ViewIcon}
              value={loading ? '...' : stats.views}
              label="Total Views"
              color="#F59E0B"
              trend={{ 
                value: stats.viewsThisWeek || 0, 
                period: 'this week' 
              }}
              delay={0.7}
            />
          </Grid>

          {/* Row 2: Enhanced Stats */}
          <Grid item xs={6} sm={6} md={3}>
            <StatsCard
              icon={AIIcon}
              value={loading ? '...' : stats.tokensUsed}
              label="AI Tokens Used"
              color="#EC4899"
              trend={{ 
                value: 'Aug 2025', 
                period: 'this month' 
              }}
              delay={0.8}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatsCard
              icon={StorageIcon}
              value={loading ? '...' : stats.storageUsed}
              label="Memory Used"
              color="#06B6D4"
              trend={{ 
                value: 'Storage', 
                period: 'total' 
              }}
              delay={0.9}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatsCard
              icon={FolderIcon}
              value={loading ? '...' : stats.publicItems}
              label="Public Items"
              color="#84CC16"
              delay={1.0}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatsCard
              icon={FolderIcon}
              value={loading ? '...' : stats.privateItems}
              label="Private Items"
              color="#6B7280"
              delay={1.1}
            />
          </Grid>
        </Grid>
      </Box>
    </DashboardContainer>
  );
};

export default Dashboard;
